/**
 * LA ESTELA de la home del Agente: la imagen del haz de luz que cae desde arriba —
 * ADR-2026-09-21-la-estela-arranca-en-el-techo-de-la-home (ajusta
 * ADR-2026-09-21-la-estela-es-una-imagen).
 *
 * Vive en un componente propio y NO adentro de `HeroAgenteHome` por una sola razón: la
 * luz tiene que arrancar en el TECHO de la home, arriba de los accesos de cuenta, y
 * desde adentro del hero eso sólo se podía hacer con un `top` negativo escrito a mano
 * que hay que recalcular cada vez que el encabezado cambie de alto. Montada en la raíz
 * de la home, `top-0` es literalmente el borde de arriba.
 *
 * La monta quien es el techo en cada ancho: `HomeMinimal` en escritorio (donde arriba
 * del hero está la fila de accesos) y la rama mobile de `InterfazV2`. En los dos casos
 * el padre tiene que ser `relative isolate`: la estela va en `-z-10` y el `isolate` la
 * encierra ahí, para que no se vaya detrás del fondo de un ancestro.
 *
 * Es un WebP con alfa (el PNG original pesaba 1,5 MB), decorativo de punta a punta:
 * `alt=""`, `aria-hidden`, `pointer-events-none`, `draggable={false}`.
 */
/**
 * La MÁSCARA de la estela. Apaga el 12% final: el PNG viene recortado (es un cuadro del
 * render, no el haz entero), así que su último renglón de píxeles todavía tiene luz y sin
 * esto se ve un corte. La luz llega entera hasta abajo y se desvanece justo en el borde.
 *
 * Arriba NO lleva fundido, ni en el teléfono: el canto de arriba de la imagen queda
 * fuera de la pantalla en los dos anchos, así que la luz "entra" desde afuera del cuadro
 * —que es lo que la hace parecer que cae del cielo—. Mobile tuvo un fundido del 18% unas
 * horas, de cuando la estela arrancaba debajo del encabezado y ese canto caía a la vista:
 * el fundido tapaba el corte, pero a cambio la luz se encendía sola en el medio de la
 * pantalla en vez de venir de arriba
 * (ADR-2026-09-21-la-estela-del-telefono-arranca-en-el-borde-de-la-pantalla).
 */
const MASCARA = 'linear-gradient(rgb(0, 0, 0) 0%, rgb(0, 0, 0) 88%, rgba(0, 0, 0, 0) 100%)';

/**
 * Cuánto tiene que SUBIR la estela en el teléfono para arrancar en el borde de la
 * pantalla. Es, exactamente, todo lo que hay entre el borde y el techo de su envoltorio:
 * el `padding-top` que `DashboardLayout` le pone al `<main>` para dejar pasar el
 * encabezado flotante —`safe-area` + el alto del header + `0.75rem` de aire + el aviso de
 * "generando de fondo" cuando lo hay— más el `pt-4` del contenedor de la home.
 *
 * Es casi la misma expresión que `mainTopPadding` en `App.tsx`, en negativo, con UNA
 * diferencia a propósito: acá el header cuenta **`5rem` y no `3.5rem`**. Los 3.5rem son
 * la caja del header; la estela sube un poco más para que el canto de arriba de la imagen
 * quede bien afuera de la pantalla y no asome ni con la `safe-area` de un teléfono con
 * notch. Si `mainTopPadding` cambia en `App.tsx`, esta expresión lo sigue — menos ese
 * sobrante, que es de acá.
 *
 * Se escribe en vez de medirse con un `ResizeObserver` porque son valores que el layout
 * ya publica y medir un adorno en cada render sale caro; el precio es esta nota.
 */
const SUBIR_EN_EL_TELEFONO =
  'calc(-1 * (env(safe-area-inset-top, 0px) + 5rem + 0.75rem + var(--aviso-generando-mobile, 0px) + 1rem))';


export interface EstelaDelAgenteProps {
  /** `mobile` la angosta y le sube un poco la opacidad. No cambia ninguna pieza. */
  variante?: 'desktop' | 'mobile';
}

export function EstelaDelAgente({ variante = 'desktop' }: EstelaDelAgenteProps) {
  const esMobile = variante === 'mobile';
  return (
    <img
      src="/images/estela/estela-agente.webp"
      alt=""
      aria-hidden="true"
      draggable={false}
      // `eager` y no `lazy`: está en el primer pantallazo, y `lazy` la hace aparecer con
      // la home ya dibujada, que se ve como un parpadeo azul.
      loading="eager"
      decoding="async"
      // ESCRITORIO: `-top-4 sm:-top-8` se come el `pt` del contenedor de la home, que
      // es todo lo que hay entre el techo del envoltorio y el borde de la página.
      // TELÉFONO: el `top` va abajo, en el `style` (`SUBIR_EN_EL_TELEFONO`), porque ahí
      // además hay que trepar el encabezado flotante y la expresión lleva `env()`.
      //
      // La opacidad va abajo, en `style`, junto con la máscara: los dos son los valores
      // con los que se calibró la luz mirando la pantalla, y separarlos —uno en una
      // utilidad de Tailwind y el otro acá— es lo que hace que se toque uno y se olvide
      // el otro.
      // En el teléfono va al 185% de la columna y sin tope, así que lo que se ve es el
      // TAJO DEL MEDIO del haz, recortado a los costados. Con `w-full` la imagen entera
      // entraba en 350px: quedaba un óvalo azul chato detrás del switch, no un haz. Más
      // ancha que esto (probado al 260%) la luz se derrama sobre "Creaciones recientes" y
      // tiñe media pantalla. Lo que la deja desbordar sin agregar scroll horizontal es el
      // `overflow-x-clip` del envoltorio que la monta — verificado: `scrollWidth` no
      // crece.
      className={`pointer-events-none absolute left-1/2 -z-10 -translate-x-1/2 select-none ${
        esMobile ? 'w-[185%] max-w-none' : '-top-4 w-full max-w-[1200px] sm:-top-8'
      }`}
      // `opacity: 0.3` — el render viene opaco y a plena intensidad es un bloque azul
      // sobre una home blanca; a 0.3 se lee como luz y deja pasar lo que hay debajo.
      // La máscara, con su porqué, está en `MASCARA`.
      style={{
        opacity: 0.3,
        maskImage: MASCARA,
        WebkitMaskImage: MASCARA,
        ...(esMobile ? { top: SUBIR_EN_EL_TELEFONO } : null),
      }}
    />
  );
}

export default EstelaDelAgente;
